import React, { useState, useEffect, useRef } from "react";
import { Box, Typography, Container } from "@mui/material";

import agendaImage from "../../../assets/images/landing/clinifica_agenda.png";
import perfilImage from "../../../assets/images/landing/clinifica_perfil.png";
import cobrosImage from "../../../assets/images/landing/clinifica_pago.png";

const FeatureBox = ({ image, title, description, delay }) => {
  const [show, setShow] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [hasBeenShown, setHasBeenShown] = useState(false);
  const boxRef = useRef(null);
  const lastScrollY = useRef(0);

  // IntersectionObserver optimizado
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        const currentScrollY = window.scrollY;
        const scrollingUp = currentScrollY < lastScrollY.current;
        
        setIsVisible(entry.isIntersecting);
        
        // Si estamos scrolleando hacia arriba y salimos del viewport, resetear
        if (!entry.isIntersecting && scrollingUp) {
          setHasBeenShown(false);
          setShow(false);
        }
        
        lastScrollY.current = currentScrollY;
      },
      {
        threshold: 0,
        rootMargin: "0px 0px 0px 0px",
      }
    );

    if (boxRef.current) {
      observer.observe(boxRef.current);
    }

    return () => {
      if (boxRef.current) {
        observer.unobserve(boxRef.current);
      }
    };
  }, []);

  // Efecto modificado para mostrar/ocultar
  useEffect(() => {
    let timer;
    
    if (isVisible && !show) {
      // Mostrar con delay cuando entra en vista
      timer = setTimeout(() => {
        setShow(true);
        setHasBeenShown(true);
      }, delay);
    } else if (!isVisible && show) {
      // Solo ocultar si no se ha mostrado completamente o si estamos scrolleando hacia arriba
      if (!hasBeenShown) {
        timer = setTimeout(() => setShow(false), 150);
      }
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [delay, isVisible, show, hasBeenShown]);

  return (
    <Box
      ref={boxRef}
      sx={{
        backgroundColor: "#e0ecfc",
        borderRadius: "16px",
        padding: "24px",
        boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
        // Para móvil mantener el ancho original
        width: { xs: "90%", sm: "320px" },
        // Para PC usar flex para que todas tengan el mismo ancho
        flex: { md: "1 1 0" },
        maxWidth: { md: "400px" }, // Limitar el ancho máximo en PC
        minHeight: { xs: "520px", md: "580px" }, // Altura mínima mayor en PC para igualar
        opacity: show ? 1 : 0,
        transform: show ? "translateY(0)" : "translateY(60px)",
        transition: "opacity 0.8s ease-out, transform 0.8s ease-out",
        display: "flex",
        flexDirection: "column",
        border: "1px solid rgba(70, 105, 232, 0.1)",
        visibility: show ? "visible" : "hidden",
      }}
    >
      {/* Título */}
      <Typography
        sx={{
          fontFamily: "Quicksand-Bold",
          fontSize: { xs: "18px", md: "24px" },
          fontWeight: "bold",
          color: "#0A56A2",
          mb: 1,
        }}
      >
        {title}
      </Typography>

      {/* Descripción - altura fija en PC para igualar todas */}
      <Typography
        sx={{
          fontFamily: "Quicksand-Medium",
          fontSize: { xs: "13px", md: "16px" },
          color: "#333333",
          lineHeight: 1.6,
          // En móvil flex: 1, en PC altura fija para igualar
          flex: { xs: 1 },
          minHeight: { md: "140px" }, // Altura fija en PC basada en el texto más largo
          mb: { md: 2 }, // Margen bottom en PC
        }}
      >
        {description}
      </Typography>

      {/* Imagen principal */}
      <Box
        sx={{
          width: "100%",
          borderRadius: "12px",
          overflow: "hidden",
          mt: { xs: 3, md: "auto" }, // En PC usar auto para que se ajuste
          backgroundColor: "#f5f5f5",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flex: { md: "1 1 auto" }, // En PC que la imagen ocupe el espacio restante
          minHeight: { md: "200px" }, // Altura mínima para la imagen en PC
        }}
      >
        {image ? (
          <img
            src={image}
            alt={title}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        ) : (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              backgroundColor: "#e3f2fd",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "12px",
            }}
          >
            <Typography
              sx={{
                color: "#4669E8",
                fontSize: "14px",
                fontFamily: "Quicksand-Regular",
              }}
            >
              Imagen
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const AnimatedFeatureBoxes = () => {
  const features = [
    {
      image: agendaImage,
      title: "Agenda a tu medida",
      description: "Abre o cierra tu agenda, reagenda fácilmente, bloquea horarios, define descansos entre sesiones y atiende online, presencial o a domicilio.",
      delay: 200,
    },
    {
      image: perfilImage,
      title: "Perfil profesional",
      description: "Mejora tu visibilidad, llega a más pacientes y crea una reputación en línea, permite que tus pacientes se agenden solos las 24 horas los 7 días de la semana.",
      delay: 400,
    },
    {
      image: cobrosImage,
      title: "Cobros y boletas",
      description: "Activa pagos anticipados o cobra directamente a tus pacientes. Emite boletas de honorario sin complicaciones.",
      delay: 600,
    },
  ];

  return (
    <Container maxWidth="xl" sx={{mb: { xs: 10, md: 15 } }}>
      <Box>
        {/* Título principal */}
        <Typography
          sx={{
            fontFamily: "Poppins-SemiBold",
            fontSize: { xs: "24px", md: "36px" },
            color: "#141414",
            textAlign: { xs: "center", md: "center" },
            mb: { xs: 4, md: 6 },
          }}
        >
          Profesionaliza la gestión de tus pacientes
        </Typography>

        {/* Layout responsive */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" }, // En PC: fila horizontal
            alignItems: { xs: "center", md: "stretch" }, // En PC: stretch para altura igual
            justifyContent: "center",
            gap: { xs: 4, md: 3, lg: 4 }, // Gap entre cajas
            minHeight: { xs: "auto", md: "600px" },
          }}
        >
          {/* Layout móvil - Columna única */}
          <Box
            sx={{
              display: { xs: "flex", md: "none" },
              flexDirection: "column",
              gap: 4,
              width: "100%",
              alignItems: "center",
            }}
          >
            {features.map((feature, index) => (
              <FeatureBox
                key={index}
                image={feature.image}
                title={feature.title}
                description={feature.description}
                delay={feature.delay}
              />
            ))}
          </Box>

          {/* Layout PC - Fila horizontal con las 3 cajas */}
          {features.map((feature, index) => (
            <Box
              key={index}
              sx={{
                display: { xs: "none", md: "flex" }, // Solo visible en PC
                flex: "1 1 0", // Todas las cajas ocupan el mismo ancho
                maxWidth: "400px", // Limitar ancho máximo
              }}
            >
              <FeatureBox
                image={feature.image}
                title={feature.title}
                description={feature.description}
                delay={feature.delay}
              />
            </Box>
          ))}
        </Box>
      </Box>
    </Container>
  );
};

export default AnimatedFeatureBoxes;
